import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  listRoles,
  listUserAssignments,
  bulkAssignAgencyRolesToUser,
  bulkAssignClinicRolesToUser,
} from '../../../services/api';
import type {
  RoleResponse,
  RoleAssignmentResponse,
} from '../../../services/api';
import type { OrgMemberResponse } from '../../clinician-manager/types';
import {
  collectAssignableClinicIds,
  filterUserVisibleRoles,
} from '../../../utils/rbacRoles';
import {
  buildInitialClinicSelectionFromAssignments,
  type ClinicSelectionMap,
} from './clinicRoleSelection';

// ─── Types ───────────────────────────────────────────────────────────────────

/** role_id → whether it's selected (agency scope) */
type AgencySelectionMap = Map<string, boolean>;

// ─── Hook ────────────────────────────────────────────────────────────────────

export type RoleAssignmentDialogState = ReturnType<
  typeof useRoleAssignmentDialog
>;

export class RoleAssignmentSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RoleAssignmentSaveError';
  }
}

type RoleAssignmentDialogOptions = {
  /**
   * Clinic IDs hidden from the role picker. Existing clinic-scoped assignments
   * at these clinics are re-sent on save so they are not reconciled away.
   */
  preserveRoleAssignmentsForClinicIds?: ReadonlySet<string>;
};

function mergeClinicRoleAssignments(
  visibleAssignments: Array<{ role_id: string; clinic_id: string }>,
  preservedAssignments: Array<{ role_id: string; clinic_id: string }>
): Array<{ role_id: string; clinic_id: string }> {
  const merged = new Map<string, { role_id: string; clinic_id: string }>();
  for (const assignment of [...visibleAssignments, ...preservedAssignments]) {
    merged.set(`${assignment.role_id}:${assignment.clinic_id}`, assignment);
  }
  return [...merged.values()];
}

export function useRoleAssignmentDialog(
  member: OrgMemberResponse | null,
  onSuccess: () => void,
  onBeforeSave?: () => Promise<void>,
  onAfterSave?: () => Promise<void>,
  options?: RoleAssignmentDialogOptions
) {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [agencyRoles, setAgencyRoles] = useState<RoleResponse[]>([]);
  const [clinicRoles, setClinicRoles] = useState<RoleResponse[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<
    RoleAssignmentResponse[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** role_id → selected for agency-scoped roles */
  const [agencySelection, setAgencySelection] = useState<AgencySelectionMap>(
    new Map()
  );

  /** role_id → Set<clinic_id> for clinic-scoped roles */
  const [clinicSelection, setClinicSelection] = useState<ClinicSelectionMap>(
    new Map()
  );

  // ── Load roles + current assignments ──

  useEffect(() => {
    if (!member?.roger_user_id) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [rolesRes, assignmentsRes] = await Promise.all([
          listRoles({
            query: { limit: 500 },
            throwOnError: true,
          }),
          listUserAssignments({
            path: { user_id: member.roger_user_id! },
            throwOnError: true,
          }),
        ]);

        if (cancelled) return;

        const roles: RoleResponse[] = rolesRes.data ?? [];
        const assignments: RoleAssignmentResponse[] = assignmentsRes.data ?? [];

        setAgencyRoles(
          filterUserVisibleRoles(roles.filter(r => r.scope === 'agency'))
        );
        setClinicRoles(
          filterUserVisibleRoles(roles.filter(r => r.scope === 'clinic'))
        );
        setCurrentAssignments(assignments);

        // Build initial selection from current assignments
        const newAgencySelection: AgencySelectionMap = new Map();

        for (const a of assignments) {
          if (a.clinic_id == null) {
            // Agency-scoped assignment
            newAgencySelection.set(a.role_id, true);
          }
        }

        setAgencySelection(newAgencySelection);
        setClinicSelection(
          buildInitialClinicSelectionFromAssignments(
            assignments,
            options?.preserveRoleAssignmentsForClinicIds
          )
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load role assignment data:', err);
          showError('Unable to load role data. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    member?.roger_user_id,
    options?.preserveRoleAssignmentsForClinicIds,
    showError,
  ]);

  // ── Agency role toggles ──

  const toggleAgencyRole = useCallback((roleId: string) => {
    setAgencySelection(prev => {
      const next = new Map(prev);
      next.set(roleId, !prev.get(roleId));
      return next;
    });
  }, []);

  // ── Clinic role toggles ──

  const toggleClinicForRole = useCallback(
    (roleId: string, clinicId: string) => {
      setClinicSelection(prev => {
        const next = new Map(prev);
        const clinics = new Set(prev.get(roleId) ?? []);
        if (clinics.has(clinicId)) {
          clinics.delete(clinicId);
        } else {
          clinics.add(clinicId);
        }
        next.set(roleId, clinics);
        return next;
      });
    },
    []
  );

  const selectAllClinicsForRole = useCallback(
    (roleId: string, allClinicIds: string[]) => {
      setClinicSelection(prev => {
        const next = new Map(prev);
        next.set(roleId, new Set(allClinicIds));
        return next;
      });
    },
    []
  );

  const clearClinicsForRole = useCallback((roleId: string) => {
    setClinicSelection(prev => {
      const next = new Map(prev);
      next.set(roleId, new Set());
      return next;
    });
  }, []);

  const assignableClinicIds = useMemo((): Set<string> | undefined => {
    if (isLoading) return undefined;
    const ids = collectAssignableClinicIds(clinicRoles, clinicSelection);
    if (!ids) return undefined;

    const hiddenClinicIds = options?.preserveRoleAssignmentsForClinicIds;
    if (!hiddenClinicIds?.size) return ids;

    return new Set([...ids].filter(clinicId => !hiddenClinicIds.has(clinicId)));
  }, [
    isLoading,
    clinicRoles,
    clinicSelection,
    options?.preserveRoleAssignmentsForClinicIds,
  ]);

  // ── Save ──

  const handleSave = useCallback(async () => {
    if (!member?.roger_user_id) return;

    setIsSaving(true);
    try {
      if (onBeforeSave) {
        await onBeforeSave();
      }

      const selectedAgencyRoleIds = [...agencySelection.entries()]
        .filter(([, selected]) => selected)
        .map(([roleId]) => roleId);

      const visibleClinicAssignments = [...clinicSelection.entries()].flatMap(
        ([roleId, clinicIds]) =>
          [...clinicIds].map(clinicId => ({
            role_id: roleId,
            clinic_id: clinicId,
          }))
      );

      const preservedClinicIds =
        options?.preserveRoleAssignmentsForClinicIds ?? new Set<string>();
      const preservedClinicAssignments = currentAssignments
        .filter(
          assignment =>
            assignment.clinic_id != null &&
            preservedClinicIds.has(assignment.clinic_id)
        )
        .map(assignment => ({
          role_id: assignment.role_id,
          clinic_id: assignment.clinic_id!,
        }));

      const clinicAssignments = mergeClinicRoleAssignments(
        visibleClinicAssignments,
        preservedClinicAssignments
      );

      await Promise.all([
        bulkAssignAgencyRolesToUser({
          path: { user_id: member.roger_user_id },
          body: { role_ids: selectedAgencyRoleIds },
          throwOnError: true,
        }),
        bulkAssignClinicRolesToUser({
          path: { user_id: member.roger_user_id },
          body: { assignments: clinicAssignments },
          throwOnError: true,
        }),
      ]);

      if (onAfterSave) {
        await onAfterSave();
      }

      showSuccess('Role assignments updated successfully.');
      onSuccess();
    } catch (err) {
      console.error('Failed to save role assignments:', err);
      if (!(err instanceof RoleAssignmentSaveError)) {
        showError('Failed to save role assignments. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    member,
    agencySelection,
    clinicSelection,
    currentAssignments,
    options?.preserveRoleAssignmentsForClinicIds,
    showSuccess,
    showError,
    onSuccess,
    onBeforeSave,
    onAfterSave,
  ]);

  return {
    agencyRoles,
    clinicRoles,
    currentAssignments,
    isLoading,
    isSaving,
    agencySelection,
    clinicSelection,
    assignableClinicIds,
    toggleAgencyRole,
    toggleClinicForRole,
    selectAllClinicsForRole,
    clearClinicsForRole,
    handleSave,
  };
}
